import { useCallback, useMemo, useRef, useState } from "react";

type StorableValue = string | number | boolean | null | StorableObject | StorableArray;
type StorableObject = { [k: string]: StorableValue };
type StorableArray = StorableValue[];

function useStoredState<T extends StorableValue>(
  key: string,
  storageType: 'local' | 'session',
  initialValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [reload, setReload] = useState<number>(0);
  const realKey = useMemo(() => `uss__${key}__key`, [key]);
  const storage = STORAGE[storageType];
  if (!storage) throw new Error(`Invalid storage type ${storageType}`);
  const ref = useRef<T>(initialValue);

  const value = useMemo(() => {
    const valueStr = storage.getValue(realKey);
    if (valueStr) {
      try {
        const v = JSON.parse(valueStr);
        ref.current = v;
        return v;
      } catch {
        // fall back to initialValue on corrupt data
      }
    }
    ref.current = initialValue;
    return initialValue;
  }, [reload, storage]);

  const setter = useCallback((value: T | ((current: T) => T)) => {
    const nextValue = (typeof value === 'function')
      ? (value as (current: T) => T)(ref.current)
      : value;
    try {
      storage.setValue(realKey, JSON.stringify(nextValue));
      ref.current = nextValue;
      setReload(x => (x + 1) % 10000);
    } catch (err) {
      console.error(`Storage sync failed for key "${key}":`, err);
    }
  }, [key, realKey, storage, ref]);

  return [value, setter];
}

const STORAGE = {
  local: {
    getValue: (x: string) => localStorage.getItem(x),
    setValue: (x: string, v: string) => localStorage.setItem(x, v),
  },
  session: {
    getValue: (x: string) => sessionStorage.getItem(x),
    setValue: (x: string, v: string) => sessionStorage.setItem(x, v),
  },
};

export type StorageType = keyof typeof STORAGE;

export default useStoredState;
